import { z } from 'zod';
import { OptionalCodeSchema } from './common.schema.js';
import { WorkforceGroupQuerySchema } from '../filters/workforceGroup.js';

export const ShiftTypeSchema = z.enum(['FIXED', 'ROTATING', 'FLEXIBLE', 'SPLIT', 'NIGHT']);

const TimeOfDay = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:MM format');

const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
};

export const ShiftInputSchema = z
  .object({
    name: z.string().min(1).max(120),
    code: OptionalCodeSchema,
    type: ShiftTypeSchema.default('FIXED'),
    startTime: TimeOfDay,
    endTime: TimeOfDay,
    breakMinutes: z.number().int().min(0).max(480).default(60),
    graceMinutes: z.number().int().min(0).max(120).default(15),
    workDays: z.array(z.number().int().min(0).max(6)).default([0, 1, 2, 3, 4]),
    isOvernight: z.boolean().default(false),
    isRamadanShift: z.boolean().default(false),
    isActive: z.boolean().default(true),
  })
  // A shift cannot start and end at the same clock time: that's either a 0h
  // shift (meaningless) or, if read as overnight, a full 24h shift (a data
  // -entry error far more often than a real roster). Reject it outright so the
  // ambiguity never reaches totalHours / overnight derivation below.
  .refine((data) => data.startTime !== data.endTime, {
    message: 'End time must differ from start time',
    path: ['endTime'],
  })
  .transform((data) => {
    // Derive isOvernight from the times rather than trusting the incoming flag,
    // which can desync from start/end (e.g. a client that forgets to tick the
    // box for a 22:00→06:00 shift). end <= start ⇒ the shift crosses midnight.
    // (end == start is already rejected above, so this is strictly end < start.)
    const startMin = toMinutes(data.startTime);
    const endMin = toMinutes(data.endTime);
    const isOvernight = endMin <= startMin;

    // Compute totalHours from start, end (wrapping past midnight when
    // overnight), then subtract the unpaid break.
    let minutes = endMin - startMin;
    if (isOvernight) minutes += 24 * 60;
    minutes -= data.breakMinutes;
    const totalHours = Math.max(0, minutes / 60);
    return { ...data, isOvernight, totalHours: Number(totalHours.toFixed(2)) };
  });
export type ShiftInput = z.infer<typeof ShiftInputSchema>;

export const ShiftAssignmentInputSchema = z.object({
  employeeId: z.string().min(1),
  effectiveFrom: z.union([z.string().datetime(), z.string().date()]),
  effectiveTo: z.union([z.string().datetime(), z.string().date()]).optional().nullable(),
});
export type ShiftAssignmentInput = z.infer<typeof ShiftAssignmentInputSchema>;

export const ScheduleEntrySchema = z.object({
  employeeId: z.string().min(1),
  date: z.string().date(),
  shiftId: z.string().optional().nullable(),
  isOff: z.boolean().default(false),
  notes: z.string().max(500).optional().nullable(),
});
export type ScheduleEntry = z.infer<typeof ScheduleEntrySchema>;

export const ScheduleBulkInputSchema = z.object({
  entries: z.array(ScheduleEntrySchema).min(1).max(500),
});
export type ScheduleBulkInput = z.infer<typeof ScheduleBulkInputSchema>;

// Assign one shift to many employees across a date range in a single request —
// the "range assignment" primitive. The server materialises Schedule rows (the
// engine already reads those) so the assignment actually drives attendance,
// chunking the writes. `mode`:
//   - 'workdays' (default) — a row only on days whose weekday is in the shift's
//     workDays (a rest day simply gets no row, matching a no-schedule day).
//   - 'all' — a row on every day in the range.
export const ScheduleAssignRangeInputSchema = z.object({
  shiftId: z.string().min(1),
  employeeIds: z.array(z.string().min(1)).min(1).max(2000),
  from: z.string().date(),
  to: z.string().date(),
  mode: z.enum(['workdays', 'all']).default('workdays'),
});
export type ScheduleAssignRangeInput = z.infer<typeof ScheduleAssignRangeInputSchema>;

export const ScheduleQuerySchema = z.object({
  from: z.string().date(),
  to: z.string().date(),
  employeeId: z.string().optional(),
  departmentId: z.string().optional(),
});
export type ScheduleQuery = z.infer<typeof ScheduleQuerySchema>;

// Query for the CSV export of a week's schedule. Same shape as the list query
// minus employeeId — the export is roster-wide across a date range, optionally
// scoped to one department (and always to the caller's team for MANAGER).
export const ScheduleExportQuerySchema = z.object({
  from: z.string().date(),
  to: z.string().date(),
  departmentId: z.string().optional(),
  // The grid's other three filters (merge E7) — the roster's own search,
  // position and workforce group, as the schedule page sends them.
  q: z.string().trim().max(80).optional(),
  designationId: z.string().optional(),
  group: WorkforceGroupQuerySchema.optional(),
  format: z.enum(['csv']).optional(),
});
export type ScheduleExportQuery = z.infer<typeof ScheduleExportQuerySchema>;

// One CSV row the client parses out of the uploaded file and posts as JSON.
// `date` is a loose string here (NOT z.string().date()) so a single bad date
// yields a per-row 'error' verdict from the service instead of failing the
// whole request at the validation middleware. shiftCode/shiftName resolve to a
// shift; a blank shift with isOff true is an OFF row.
export const ScheduleImportRowSchema = z.object({
  employeeCode: z.string().min(1),
  date: z.string(),
  shiftCode: z.string().optional().nullable(),
  shiftName: z.string().optional().nullable(),
  isOff: z.boolean().optional(),
});
export type ScheduleImportRow = z.infer<typeof ScheduleImportRowSchema>;

export const ScheduleImportInputSchema = z.object({
  rows: z.array(ScheduleImportRowSchema).min(1).max(5000),
  dryRun: z.boolean(),
});
export type ScheduleImportInput = z.infer<typeof ScheduleImportInputSchema>;
